/**
 * Configuration Comparison Service
 *
 * PURPOSE:
 * Provides utilities for comparing configurations to detect duplicates.
 * Used to prevent duplicate processing when bond/millage/DRF combinations match existing configurations.
 *
 * ARCHITECTURE:
 * Pure functions that compare configuration data structures.
 * No side effects - returns comparison results only.
 *
 * IMPORTANT: Configurations with overrides (custom millage/taxable values) are treated
 * as distinct from standard configurations, even if their inputs match. This prevents
 * accidentally switching to a custom configuration when the user expects a new standard one.
 */

import type { BondSeries, Configuration, DRFInputsForm, MillageInputsForm } from '~/types';
import { hasAnyOverrides } from './override-service';

/**
 * Compares two bond series arrays for equality
 * Normalizes by sorting by name (tax year), then compares key properties.
 * Note: Bond IDs are NOT compared because they are auto-generated UUIDs.
 */
function areBondSeriesEqual(bonds1: BondSeries[], bonds2: BondSeries[]): boolean {
  if (bonds1.length !== bonds2.length) {
    return false;
  }

  // Sort by name (tax year) for consistent comparison
  // Note: We do NOT compare by id because IDs are auto-generated UUIDs.
  // Two bonds with the same settings but different IDs should be considered equal.
  const sorted1 = [...bonds1].sort((a, b) => a.name.localeCompare(b.name));
  const sorted2 = [...bonds2].sort((a, b) => a.name.localeCompare(b.name));

  for (let i = 0; i < sorted1.length; i++) {
    const bond1 = sorted1[i];
    const bond2 = sorted2[i];

    // Normalize target amounts: 0, undefined, null, empty string all mean "no target"
    const target1 = bond1.targetInformation?.principalTargetAmount || 0;
    const target2 = bond2.targetInformation?.principalTargetAmount || 0;

    // Compare key properties that define bond identity (NOT the auto-generated id)
    if (
      bond1.name !== bond2.name ||
      bond1.issueDate !== bond2.issueDate ||
      bond1.firstInterestPaymentDate !== bond2.firstInterestPaymentDate ||
      bond1.firstPrincipalPaymentDate !== bond2.firstPrincipalPaymentDate ||
      bond1.finalPrincipalPaymentDate !== bond2.finalPrincipalPaymentDate ||
      target1 !== target2 ||
      // Only compare targetType if there's actually a target amount
      (target1 > 0 && bond1.targetInformation?.targetType !== bond2.targetInformation?.targetType)
    ) {
      return false;
    }

    // Compare interest rate escalation settings
    const bond1WithEscalation = bond1 as BondSeries & {
      __interestRateEscalation?: { startRate: number; incrementRate: number; maxRate: number };
    };
    const bond2WithEscalation = bond2 as BondSeries & {
      __interestRateEscalation?: { startRate: number; incrementRate: number; maxRate: number };
    };

    const escalation1 = bond1WithEscalation.__interestRateEscalation;
    const escalation2 = bond2WithEscalation.__interestRateEscalation;

    if (escalation1 && escalation2) {
      if (
        escalation1.startRate !== escalation2.startRate ||
        escalation1.incrementRate !== escalation2.incrementRate ||
        escalation1.maxRate !== escalation2.maxRate
      ) {
        return false;
      }
    } else if (escalation1 !== escalation2) {
      // One has escalation, the other doesn't
      return false;
    }
  }

  return true;
}

/**
 * Compares two millage inputs forms for equality
 */
function areMillageInputsEqual(millage1: MillageInputsForm, millage2: MillageInputsForm): boolean {
  return (
    millage1.peakMillage === millage2.peakMillage &&
    millage1.peakMillageDuration === millage2.peakMillageDuration &&
    millage1.minimumMillage === millage2.minimumMillage &&
    millage1.minimumMillageDuration === millage2.minimumMillageDuration
  );
}

/**
 * Compares two DRF inputs forms for equality
 */
function areDRFInputsEqual(drf1: DRFInputsForm, drf2: DRFInputsForm): boolean {
  return (
    drf1.startingBalance === drf2.startingBalance &&
    drf1.minimumBalance === drf2.minimumBalance &&
    drf1.maximumBalance === drf2.maximumBalance
  );
}

/**
 * Compares two configurations for equality
 *
 * Two configurations are considered equal if:
 * - Bond series match (same bonds, normalized by id)
 * - Millage inputs match (all 4 fields)
 * - DRF inputs match (all 3 fields)
 *
 * Note: Does NOT compare candidates, status, or orderId - only the input configuration
 */
export function areConfigurationsEqual(config1: Configuration, config2: Configuration): boolean {
  // Compare bond series
  if (!areBondSeriesEqual(config1.bondSeries, config2.bondSeries)) {
    return false;
  }

  // Compare millage inputs
  if (!areMillageInputsEqual(config1.millageInputs, config2.millageInputs)) {
    return false;
  }

  // Compare DRF inputs
  if (!areDRFInputsEqual(config1.drfInputs, config2.drfInputs)) {
    return false;
  }

  return true;
}

/**
 * Finds a matching configuration from a list based on bonds, millage, DRF inputs, and overrides
 *
 * Two configurations match only if ALL their content matches - including overrides.
 * A configuration with custom millage overrides is fundamentally different from one without.
 *
 * @param configurations - List of existing configurations to search
 * @param bonds - Bond series to match
 * @param millage - Millage inputs to match
 * @param drf - DRF inputs to match
 * @param overrides - Overrides to match (null/undefined means no overrides)
 * @returns Matching configuration or null if no match found
 */
export function findMatchingConfiguration(
  configurations: Configuration[],
  bonds: BondSeries[],
  millage: MillageInputsForm,
  drf: DRFInputsForm,
  overrides?: Configuration['overrides']
): Configuration | null {
  for (const config of configurations) {
    // All content must match - bonds, millage, DRF, AND overrides
    if (
      areBondSeriesEqual(config.bondSeries, bonds) &&
      areMillageInputsEqual(config.millageInputs, millage) &&
      areDRFInputsEqual(config.drfInputs, drf) &&
      areOverridesEqual(config.overrides, overrides)
    ) {
      return config;
    }
  }

  return null;
}

/**
 * Compares two override objects for equality
 * Both having no overrides = equal
 * One has overrides, one doesn't = not equal
 * Both have overrides = compare the override content
 */
function areOverridesEqual(
  overrides1: Configuration['overrides'],
  overrides2: Configuration['overrides']
): boolean {
  const has1 = hasAnyOverrides(overrides1);
  const has2 = hasAnyOverrides(overrides2);

  // Both have no overrides - equal
  if (!has1 && !has2) {
    return true;
  }

  // One has overrides, one doesn't - not equal
  if (has1 !== has2) {
    return false;
  }

  // Both have overrides - compare content
  // Compare taxable value overrides
  const tv1 = overrides1?.taxableValue?.byYear ?? {};
  const tv2 = overrides2?.taxableValue?.byYear ?? {};
  if (!areOverridesByYearEqual(tv1, tv2)) {
    return false;
  }

  // Compare millage overrides
  const m1 = overrides1?.millage?.byYear ?? {};
  const m2 = overrides2?.millage?.byYear ?? {};
  if (!areOverridesByYearEqual(m1, m2)) {
    return false;
  }

  return true;
}

/**
 * Compares two byYear override maps for equality
 */
function areOverridesByYearEqual(
  byYear1: Record<string | number, unknown>,
  byYear2: Record<string | number, unknown>
): boolean {
  const keys1 = Object.keys(byYear1);
  const keys2 = Object.keys(byYear2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!(key in byYear2)) {
      return false;
    }
    // Deep compare the override objects
    if (JSON.stringify(byYear1[key]) !== JSON.stringify(byYear2[key])) {
      return false;
    }
  }

  return true;
}
